import { UserResponse } from '@descope/core-js-sdk'
import { useSession, useUser } from '@descope/react-sdk'
import { jwtDecode } from 'jwt-decode'
import { type ReactNode, createContext, createElement, useContext, useMemo } from 'react'

type OrgDetails = {
  orgId: string
  orgName: string
}

type OrgDetailsState = {
  orgDetails: OrgDetails | null
  isUserLoading: boolean
  isSessionLoading: boolean
}

const emptyOrgDetails: OrgDetailsState = {
  orgDetails: null,
  isUserLoading: false,
  isSessionLoading: false,
}

const OrgDetailsContext = createContext<OrgDetailsState | null>(null)

export const OrgDetailsProvider = ({ children }: { children: ReactNode }) => {
  const { orgDetails, isUserLoading, isSessionLoading } = useOrgDetails()
  const value = useMemo(
    () => ({ orgDetails, isUserLoading, isSessionLoading }),
    [orgDetails, isUserLoading, isSessionLoading],
  )
  return createElement(OrgDetailsContext.Provider, { value }, children)
}

export const useOrgDetails = () => {
  const { user, isUserLoading } = useUser()
  const { isSessionLoading, sessionToken } = useSession()
  const orgDetails = useOrgIdFromToken(user, sessionToken)
  return { orgDetails, isUserLoading, isSessionLoading }
}

export const useOptionalOrgDetails = () => {
  // Safe outside AuthProvider: stories and isolated tests have no Descope tree.
  return useContext(OrgDetailsContext) ?? emptyOrgDetails
}

export const useOrgIdFromToken = (user: UserResponse | undefined, token: string) => {
  return useMemo(() => getOrgDetails(user, token), [user, token])
}

const getOrgDetails = (user: UserResponse | undefined, token: string): OrgDetails | null => {
  if (!user || !user.userTenants || !user.userTenants.length) {
    return null
  }

  if (token && token.length > 0) {
    try {
      const { dct } = jwtDecode<{ dct?: string }>(token)
      if (dct) {
        // select tenant from user.userTenants by tenantId that is dct
        const tenant = user.userTenants?.find((t) => t.tenantId === dct)
        if (tenant) {
          return {
            orgId: tenant.tenantId,
            orgName: tenant.tenantName,
          }
        }
      }
    } catch (error) {
      console.error('Failed to decode session token for org details', error)
    }
  }

  // fall back option, attempt to always select the same tenant
  const tenants = user.userTenants.sort((a, b) => a.tenantId.localeCompare(b.tenantId))
  return {
    orgId: tenants[0].tenantId,
    orgName: tenants[0].tenantName,
  }
}
